
import React, { useRef, useState, useMemo, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LocationSearch } from './LocationSearch_Google';
import { DateRangePicker } from './DateRangePicker';
import { InterestSelector } from './InterestSelector';
import { TravelGroupSelector } from './TravelGroupSelector';
import { TripDetails, ActivityType, TravelGroup, Itinerary } from '../../types/itinerary';
import { X, Plane, Calendar, BookOpen, Users, ChevronLeft, Edit } from 'lucide-react';
import { ItineraryPanel } from './ItineraryPanel'; 
import { PersonalizedPlannerPanel } from './PersonalizedPlannerPanel';
import { generateItinerary, loadItineraryById } from '../../services/itinerary/builder';
import { itineraryService, dataSyncService } from '../../services/dataPersistence';
import { authTokenManager } from '../../utils/authTokenManager';
import transformers from '../../services/dataPersistence/transformers';
import { Search } from 'lucide-react';
import { format } from 'date-fns';



// First, expand the WizardStep type to include saved itineraries
type WizardStep = 'initial' | 'details' | 'results' | 'personalized' | 'savedItineraries';

// Add this with other state declarations
type EntryPoint = 'saved' | 'wizard' | 'direct';

// Define the ref interface
interface PlannerWizardRefInterface {
  submitForm: (details: TripDetails) => boolean;
  populateForm: (details: TripDetails) => boolean;
  reset: () => void;  // ADD THIS
}

// Modify interface to include ref
export interface PlannerWizardProps {
  onSubmit: (details: TripDetails) => void;
  isLoading: boolean;
  error: string | null;
  itinerary: Partial<Itinerary>;
  onLocationSelect: (locationId: string) => void;
  selectedLocationId?: string;
  onLocationsUpdate?: (locations: any[]) => void;
  streamingActivity?: boolean;
  onItineraryUpdate?: (itinerary: Partial<Itinerary>, streamingActivity?: boolean) => void;
  isGeneratingItinerary: boolean;
  shouldShowResults: boolean;
  className?: string;
  onClose?: () => void;
  onBackToDetails?: () => void; // Add this
  activeDay?: number;
  plannerWizardRef?: React.RefObject<any>; // Add this line
}

// Convert to forwardRef pattern
export const PlannerWizard = forwardRef<PlannerWizardRefInterface, PlannerWizardProps>(
  (props, ref) => {
    const {
      onClose,
      onSubmit,
      isLoading = false,
      error,
      itinerary = {},
      onLocationSelect,
      selectedLocationId,
      onLocationsUpdate,
      streamingActivity = false,
      activeDay,
      className,
      onItineraryUpdate,
      isGeneratingItinerary = false,
      shouldShowResults = false,
      plannerWizardRef,  // Add this line
      onBackToDetails // Add this
    } = props;

    console.log('[PlannerWizard] Rendering with props', {
      hasItineraryData: !!itinerary?.tripDetails?.destination,
      daysCount: itinerary?.days?.length || 0,
      isGeneratingItinerary,
      shouldShowResults
    });

    const itineraryRef = useRef(itinerary);
    const selectedLocationIdRef = useRef(selectedLocationId);
    
    const [step, setStep] = useState<WizardStep>('initial');
    const [formData, setFormData] = useState<{
      destination: string;
      startDate: Date | null;
      endDate: Date | null;
      travelGroup?: TravelGroup;
      interests: ActivityType[];
      savedItineraryId?: string;
    }>({
      destination: '',
      startDate: null,
      endDate: null,
      interests: []
    });
    
    // Flag to control when to show the itinerary content
    const [showItineraryContent, setShowItineraryContent] = useState(false);
    // Flag to track if form was submitted
    const [formSubmitted, setFormSubmitted] = useState(false);

    // Add a new state to track available saved itineraries
    const [savedItineraries, setSavedItineraries] = useState<Itinerary[]>([]);

    const [isLoadingSavedItinerary, setIsLoadingSavedItinerary] = useState(false);

    const [savedItineraryId, setSavedItineraryId] = useState<string | null>(null);
    
    // State variables
    const [isLoadingItineraries, setIsLoadingItineraries] = useState(false);
    const [itineraryLoadError, setItineraryLoadError] = useState<string | null>(null);

    const [hasStoredItineraries, setHasStoredItineraries] = useState(false);

    const [isNavigatingBack, setIsNavigatingBack] = useState(false);

    const [entryPoint, setEntryPoint] = useState<EntryPoint>('direct');

    const blockAutoTransitionRef = useRef(false);

    const prevStepRef = useRef(step);

    const [cameFromStep, setCameFromStep] = useState<WizardStep>('initial');



    // Add this to the top of your PlannerWizard component or in a separate CSS file
    const textStyles = {
      // Base text styles
      base: {
        fontFamily: 'var(--chat-font-family)',
        fontSize: 'var(--planner-body-size)',
        fontWeight: 'var(--planner-font-weight-normal)',
        lineHeight: 'var(--chat-line-height)',
        color: 'var(--planner-text-primary)'
      },
      // Header styles
      h2: {
        fontSize: 'var(--planner-heading-size)',
        fontWeight: 'var(--planner-font-weight-semibold)'
      },
      h3: {
        fontSize: 'var(--planner-subheading-size)',
        fontWeight: 'var(--planner-font-weight-medium)'
      },
      h4: {
        fontSize: 'var(--planner-body-size)',
        fontWeight: 'var(--planner-font-weight-medium)'
      },
      // Text sizes
      large: {
        fontSize: 'var(--planner-subheading-size)'
      },
      small: {
        fontSize: 'var(--planner-small-size)'
      },
      xs: {
        fontSize: 'calc(var(--chat-font-size) * 0.833)' // 10px if base is 12px
      },
      // Font weights
      medium: {
        fontWeight: 'var(--planner-font-weight-medium)'
      },
      semibold: {
        fontWeight: 'var(--planner-font-weight-semibold)'
      },
      // Colors
      secondary: {
        color: 'var(--planner-text-secondary)'
      },
      tertiary: {
        color: 'var(--planner-text-tertiary)'
      }
    };

    // Add useImperativeHandle to expose methods through ref
    useImperativeHandle(ref, () => ({
      submitForm: (details: TripDetails) => {
        if (!details) return false;
        
        console.log('[PlannerWizard] Direct form submission with:', details);
        
        // Set form data first to ensure UI consistency
        setFormData({
          destination: details.destination || '',
          startDate: details.startDate ? new Date(details.startDate) : null,
          endDate: details.endDate ? new Date(details.endDate) : null,
          travelGroup: details.travelGroup,
          interests: details.preferences?.activityTypes || []
        });
        
        // Trigger submission
        onSubmit(details);
        setFormSubmitted(true);
        return true;
      },
      
      populateForm: (details: TripDetails) => {
        if (!details) return false;
        
        console.log('[PlannerWizard] Populating form with:', details);
        
        // Set form data
        setFormData({
          destination: details.destination || '',
          startDate: details.startDate ? new Date(details.startDate) : null,
          endDate: details.endDate ? new Date(details.endDate) : null,
          travelGroup: details.travelGroup,
          interests: details.preferences?.activityTypes || []
        });
        
        // Show the details step
        setStep('details');
        return true;
      },
      // Update the reset method in useImperativeHandle
      reset: () => {
        blockAutoTransitionRef.current = true;
        
        setStep('initial');
        setFormData({
          destination: '',
          startDate: null,
          endDate: null,
          interests: []
        });
        setShowItineraryContent(false);
        setFormSubmitted(false);
        setSavedItineraryId(null);
        
        setTimeout(() => {
          blockAutoTransitionRef.current = false;
        }, 1000);
      }
    }));

  // Simplified loadSavedItineraries function - Database fetch only
  const loadSavedItineraries = useCallback(async () => {
    // Check if authenticated
    if (!authTokenManager.isAuthenticated()) {
      console.log('[PlannerWizard] Not authenticated, cannot fetch itineraries');
      return false;
    }
    
    try {
      setIsLoadingItineraries(true);
      setItineraryLoadError(null);
      
      console.log('[PlannerWizard] Fetching itineraries from database');
      
      // Direct fetch from endpoint
      const response = await fetch('/api/user-data/itineraries', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authTokenManager.getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch itineraries: ${response.status}`);
      }
      
      const itinerariesData = await response.json();
      
      // Transform API data to client format
      const dbItineraries = transformers.itinerariesApiToClient(itinerariesData);
      console.log(`[PlannerWizard] Loaded ${dbItineraries.length} itineraries from database`);
      
      console.log(" dbItineraries ", dbItineraries);
      // Set itineraries from database
      setSavedItineraries(dbItineraries); 
      return dbItineraries.length > 0;

    } catch (error) {
      console.error('[PlannerWizard] Error loading itineraries from database:', error);
      setItineraryLoadError('Failed to load itineraries from database.');
      return false;
    } finally {
      setIsLoadingItineraries(false);
    }
  }, []);



  // Add this logging helper at the top of PlannerWizard.tsx
  function logItineraryDetail(itinerary, prefix = "ITINERARY_DEBUG") {
    console.log(`${prefix}: Itinerary details:`, {
      hasData: !!itinerary,
      id: itinerary?.id,
      hasDestination: !!itinerary?.tripDetails?.destination,
      destination: itinerary?.tripDetails?.destination,
      daysExist: !!itinerary?.days,
      daysIsArray: Array.isArray(itinerary?.days),
      daysCount: itinerary?.days?.length || 0
    });
    
    if (itinerary?.days?.length > 0) {
      console.log(`${prefix}: First day:`, {
        dayNumber: itinerary.days[0].dayNumber,
        date: itinerary.days[0].date,
        activitiesExist: !!itinerary.days[0].activities,
        activitiesCount: itinerary.days[0].activities?.length || 0
      });
    }
  }


  // Add this helper function to determine direction
  const getAnimationDirection = (currentStep: WizardStep) => {
    const stepOrder: WizardStep[] = ['initial', 'savedItineraries', 'details', 'personalized', 'results'];
    const prevIndex = stepOrder.indexOf(prevStepRef.current);
    const currentIndex = stepOrder.indexOf(currentStep);
    
    // If going to a later step, it's forward. If going to earlier step, it's backward
    return currentIndex > prevIndex ? 'forward' : 'backward';
  };


  // Update it whenever step changes
  useEffect(() => {
    prevStepRef.current = step;
  }, [step]);

  // Update refs when props change, but don't trigger rerenders
  useEffect(() => {
    itineraryRef.current = itinerary;
  }, [itinerary]);

  useEffect(() => {
    selectedLocationIdRef.current = selectedLocationId;
  }, [selectedLocationId]);

  // Check for saved itineraries when component mounts
  useEffect(() => {
    loadSavedItineraries();
  }, [loadSavedItineraries]);


  // Update in the useEffect for transitions
// Replace or modify the transition useEffect in PlannerWizard.tsx
 /*  useEffect(() => {
    // More reliable check that verifies days data is present
    const hasItineraryData = !!itinerary?.tripDetails?.destination && 
                            Array.isArray(itinerary?.days) && 
                            itinerary?.days?.length > 0;
    
    // Check for force flags
    const forceTransition = !!itinerary?._forceShowResults || !!itinerary?.shouldShowResults;
    
    // Should transition if any of the conditions are met
    const shouldTransition = (formSubmitted || shouldShowResults || forceTransition) && 
                            (hasItineraryData || forceTransition);
    
    console.log('[PlannerWizard] Checking for auto-transition:', {
      formSubmitted,
      shouldShowResults,
      forceTransition,
      hasItineraryData,
      itineraryDays: itinerary?.days?.length || 0,
      shouldTransition,
      currentStep: step
    });
    
    if (shouldTransition && step !== 'results') {
      console.log('[PlannerWizard] Transitioning to results step');
      setStep('results');
      
      // Don't delay content display for saved itineraries
      if (forceTransition) {
        setShowItineraryContent(true);
      } else {
        // Short animation delay for new itineraries
        setTimeout(() => {
          setShowItineraryContent(true);
        }, 300);
      }
    }

    logItineraryDetail(itinerary, "[PlannerWizard][Transition]");

  }, [formSubmitted, shouldShowResults, itinerary, step]); */

  // In the auto-transition useEffect
  useEffect(() => {
    // Skip if we're navigating backwards
    if (isNavigatingBack) {
      console.log('[PlannerWizard] Skipping auto-transition - navigating back');
      return;
    }
    
    // PREVENT AUTO-TRANSITION IF USER IS ON ANY WIZARD STEP (except initial)
    if (step === 'details' || step === 'personalized' || step === 'savedItineraries') {
      console.log('[PlannerWizard] Skipping auto-transition - user is on', step);
      return; // Exit early - don't auto-transition
    }
    
    // Rest of the existing logic remains the same
    const hasItineraryData = !!itinerary?.tripDetails?.destination && 
                            Array.isArray(itinerary?.days) && 
                            itinerary?.days?.length > 0;
    
    const forceTransition = !!itinerary?._forceShowResults || !!itinerary?.shouldShowResults;
    
    const shouldTransition = (formSubmitted || shouldShowResults || forceTransition) && 
                            (hasItineraryData || forceTransition);
    
    console.log('[PlannerWizard] Checking for auto-transition:', {
      formSubmitted,
      shouldShowResults,
      forceTransition,
      hasItineraryData,
      itineraryDays: itinerary?.days?.length || 0,
      shouldTransition,
      currentStep: step
    });
    
    if (shouldTransition && step !== 'results') {
      console.log('[PlannerWizard] Transitioning to results step');
      setStep('results');
      
      if (forceTransition) {
        setShowItineraryContent(true);
      } else {
        setTimeout(() => {
          setShowItineraryContent(true);
        }, 300);
      }
    }
  }, [formSubmitted, shouldShowResults, itinerary, step, isNavigatingBack]);

    /* Uncaught ReferenceError: AutocompleteSessionToken is not defined
    at handlePredictionSelect (LocationSearch_Google.tsx:213:5) */

  // State to track if locations have been processed
  const [locationsProcessed, setLocationsProcessed] = useState(false);
    
  // Store processed locations to prevent unnecessary updates
  const processedLocationsRef = useRef<any[]>([]);

  // Memoize the onLocationSelect handler
  const handleLocationSelect = useCallback((locationId: string) => {
    onLocationSelect(locationId);
  }, [onLocationSelect]);

  const handleLocationsUpdate = useCallback((locations: any[]) => {
    if (onLocationsUpdate) {
      onLocationsUpdate(locations);
    }
  }, [onLocationsUpdate]);

  // Handle trip details update with memoization
  const handleTripDetailsUpdate = useCallback((details: any) => {
    if (onItineraryUpdate) {
      const updatedItinerary = {
        ...itinerary,
        tripDetails: {
          ...itinerary.tripDetails,
          ...details
        }
      };
      onItineraryUpdate(updatedItinerary, false);
    }
  }, [itinerary, onItineraryUpdate]);

  // Reset the locations processed flag when the itinerary changes significantly
  useEffect(() => {
    if (itinerary?.tripDetails?.destination !== itineraryRef.current?.tripDetails?.destination) {
      console.log("[PlannerWizard] Destination changed, resetting locations processed flag");
      setLocationsProcessed(false);
      processedLocationsRef.current = [];
    }
  }, [itinerary?.tripDetails?.destination]);

  // Validate form data
  const isValid = useMemo(() => {
    const hasDestination = formData.destination.trim().length > 0;
    const hasTravelGroup = !!formData.travelGroup;
    const hasValidDates = formData.startDate && formData.endDate;
    
    return hasDestination && hasTravelGroup && hasValidDates;
  }, [formData.destination, formData.travelGroup, formData.startDate, formData.endDate]);

  // When submitting from wizard form
  /* const handleSubmit = async () => {
    if (!isValid) return;
    
    setEntryPoint('wizard'); // Mark entry point
    
    const details: TripDetails = {
      destination: formData.destination,
      startDate: formData.startDate || undefined,
      endDate: formData.endDate || undefined,
      travelGroup: formData.travelGroup!,
      preferences: {
        activityTypes: formData.interests
      }
    };
    
    const enrichedDetails = savedItineraryId ? 
      { ...details, isLoadingSavedItinerary: true, savedItineraryId } : 
      details;
    
    setFormSubmitted(true);
    onSubmit(enrichedDetails);
    setSavedItineraryId(null);
  }; */
  

  const handleSubmit = async () => {
    if (!isValid) return;
    
    // When submitting from details, ensure back button returns to details
    //setCameFromStep('details');
    setEntryPoint('wizard'); // Mark entry point if you're using this elsewhere
    
    const details: TripDetails = {
      destination: formData.destination,
      startDate: formData.startDate || undefined,
      endDate: formData.endDate || undefined,
      travelGroup: formData.travelGroup!,
      preferences: {
        activityTypes: formData.interests
      }
    };
    
    const enrichedDetails = savedItineraryId ? 
      { ...details, isLoadingSavedItinerary: true, savedItineraryId } : 
      details;
    
    setFormSubmitted(true);
    onSubmit(enrichedDetails);
    
    // Navigate to results immediately
    setStep('results');
    setShowItineraryContent(true); // CHANGE THIS TO TRUE to skip loading screen
    
    // Reset the ID after submission
    setSavedItineraryId(null);
  };

  // Modified saved itinerary click handler
  // Add this useEffect inside your component
  useEffect(() => {
    if (isValid && formSubmitted) {
      handleSubmit(); // Trigger generation
      setFormSubmitted(false); // Reset flag
    }
  }, [isValid, formSubmitted]); // <-- Runs when isValid or formSubmitted changes

 // Update handleSavedItineraryClick
  const handleSavedItineraryClick = (itinerary: Itinerary) => {
    // Store the ID
    setSavedItineraryId(itinerary.id);
    setCameFromStep('savedItineraries'); // Track where we came from
    
    console.log('[PlannerWizard] Loading saved itinerary:', itinerary.id);

    // Update form data
    setFormData({
      destination: itinerary.tripDetails?.destination || '',
      startDate: new Date(itinerary.tripDetails?.startDate || ''),
      endDate: new Date(itinerary.tripDetails?.endDate || ''),
      travelGroup: itinerary.tripDetails?.travelGroup as TravelGroup,
      interests: itinerary.tripDetails?.preferences?.activityTypes || []
    });
    
    setFormSubmitted(true);
    
    // Directly navigate to results instead of relying on auto-transition
    setTimeout(() => {
      setStep('results');
      setShowItineraryContent(true);
    }, 100); // Small delay to ensure form data is set
  };

  // Go back from results to details step
  const handleBackToDetails = () => {
    console.log('[PlannerWizard] Going back to details step');
    setShowItineraryContent(false); // Hide itinerary content first
    setStep('details');
  };

  // Get the header title based on current step
  const getHeaderTitle = () => {
    switch (step) {
      case 'initial': return "Let's plan your trip";
      case 'details': return "Let's plan your trip";
      case 'personalized': return "Create your personalized plan";
      case 'results': return "Your Itinerary";
      case 'savedItineraries': return "Your Saved Itineraries";
      default: return 'Plan Your Trip';
    }
  };

  return (
    <div     
      className={`h-full flex flex-col overflow-hidden ${className || ''}`}
      style={{
        fontFamily: 'var(--chat-font-family)',
        fontSize: 'var(--planner-body-size)',
        lineHeight: 'var(--chat-line-height)',
        color: 'var(--planner-text-primary)'
      }}
    >
      {/* Header   if ((step as string) === 'results') {*/}
      {/* {step !== 'results' && ( */}
        <div className="flex items-center justify-between p-6 py-7 border-b" style={{ borderColor: 'var(--planner-border-color)' }}>
          <h2 style={{
            ...textStyles.base,
            ...textStyles.h3,
          }}>
            {getHeaderTitle()}
          </h2>
          {onClose && (
            <button
              onClick={() => {
                if (step === 'results') {
                  console.log('[PlannerWizard] User clicked back from results');
                  setIsNavigatingBack(true);
                  setShowItineraryContent(false);
                  setStep('details');
                  // Don't change cameFromStep - preserve it
                  
                  setTimeout(() => {
                    setIsNavigatingBack(false);
                  }, 500);
                } else {
                  onClose();
                }
              }}
              className="transition-colors hover:text-gray-600"
              style={{
                color: 'var(--planner-text-tertiary)'
              }}
            >
              <X style={{ width: 'var(--planner-icon-size-lg)', height: 'var(--planner-icon-size-lg)' }} />
            </button>
          )}
        </div>
      {/* )} */}

      {/* Content with sliding animation */}
      <div className="flex-1 overflow-hidden relative">
        <AnimatePresence initial={false} mode="wait">
          {/* Initial Step */}
          {step === 'initial' && (
            <motion.div 
              key="initial"
              className="absolute inset-0 flex flex-col p-6"
              initial={{ x: getAnimationDirection('initial') === 'forward' ? '100%' : '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: getAnimationDirection('initial') === 'forward' ? '-100%' : '100%' }}
              transition={{ type: 'tween', ease: 'easeInOut', duration: 0.3 }}
            >
              <div className="space-y-6 w-full max-w-md mx-auto">
                <div className="space-y-4 mt-3">
                  {/* Help me plan button */}
                  <button
                    onClick={() => {
                      setCameFromStep('initial'); // Track where we came from
                      setStep('details');
                    }}
                    className="flex items-center gap-4 transition-colors group shadow-sm"
                    style={{
                      width: 'var(--planner-button-width)',
                      padding: '1rem',
                      backgroundColor: 'white',
                      border: '1px solid var(--planner-border-color)',
                      borderRadius: '0.5rem',
                      fontFamily: 'var(--chat-font-family)',
                      fontSize: 'var(--planner-body-size)',
                      color: 'var(--planner-text-primary)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--planner-bg-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div className="flex items-center justify-center transition-colors"
                      style={{
                        width: 'var(--planner-button-icon-container)',
                        height: 'var(--planner-button-icon-container)',
                        borderRadius: '50%',
                        backgroundColor: '#dcfce7'
                      }}>
                      <Calendar style={{ width: 'var(--planner-icon-size-xs)', height: 'var(--planner-icon-size-xs)' }} className="text-green-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <h4 style={{...textStyles.base, ...textStyles.h4}}>Help me plan</h4>
                      <p style={{
                        fontFamily: 'var(--chat-font-family)',
                        fontSize: 'var(--planner-body-size)',
                        color: 'var(--planner-text-secondary)'
                      }}>
                        Get an AI Powered travel plan
                      </p>
                    </div>
                  </button>

                  {/* Create my plan button */}
                  <button
                    onClick={() => setStep('personalized')}
                    className="flex items-center gap-4 transition-colors group shadow-sm"
                    style={{
                      width: 'var(--planner-button-width)',
                      padding: '1rem',
                      backgroundColor: 'white',
                      border: '1px solid var(--planner-border-color)',
                      borderRadius: '0.5rem',
                      fontFamily: 'var(--chat-font-family)',
                      fontSize: 'var(--planner-body-size)',
                      color: 'var(--planner-text-primary)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--planner-bg-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div className="flex items-center justify-center transition-colors"
                      style={{
                        width: 'var(--planner-button-icon-container)',
                        height: 'var(--planner-button-icon-container)',
                        borderRadius: '50%',
                        backgroundColor: '#dbeafe'
                      }}>
                      <Plane style={{ width: 'var(--planner-icon-size-md)', height: 'var(--planner-icon-size-md)' }} className="text-blue-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <h4 style={{...textStyles.base, ...textStyles.h4}}>Create my plan</h4>
                      <p style={{
                        fontFamily: 'var(--chat-font-family)',
                        fontSize: 'var(--planner-body-size)',
                        color: 'var(--planner-text-secondary)'
                      }}>
                        Plan a trip from scratch
                      </p>
                    </div>
                  </button>

                  {/* My Saved Itineraries */}
                  {savedItineraries.length > 0 && (
                    <motion.button
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.1 }}
                      onClick={() => setStep('savedItineraries')}
                      className="flex items-center gap-4 transition-colors group shadow-sm"
                      style={{
                        width: 'var(--planner-button-width)',
                        padding: '1rem',
                        backgroundColor: 'white',
                        border: '1px solid var(--planner-border-color)',
                        borderRadius: '0.5rem',
                        fontFamily: 'var(--chat-font-family)',
                        fontSize: 'var(--planner-body-size)',
                        color: 'var(--planner-text-primary)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--planner-bg-hover)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <div className="flex items-center justify-center transition-colors"
                        style={{
                          width: 'var(--planner-button-icon-container)',
                          height: 'var(--planner-button-icon-container)',
                          borderRadius: '50%',
                          backgroundColor: '#f3e8ff'
                        }}>
                        <BookOpen style={{ width: 'var(--planner-icon-size-md)', height: 'var(--planner-icon-size-md)' }} className="text-purple-600" />
                      </div>
                      <div className="flex-1 text-left">
                        <h4 style={{...textStyles.base, ...textStyles.h4}}>My Itineraries</h4>
                        {isLoadingItineraries ? (
                          <div className="flex items-center gap-2" style={{
                            fontFamily: 'var(--chat-font-family)',
                            fontSize: 'var(--planner-body-size)',
                            color: 'var(--planner-text-secondary)'
                          }}>
                            <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            <span>Getting your travel plans...</span>
                          </div>
                        ) : (
                          <p style={{
                            fontFamily: 'var(--chat-font-family)',
                            fontSize: 'var(--planner-body-size)',
                            color: 'var(--planner-text-secondary)'
                          }}>
                            {`${savedItineraries.length} saved ${savedItineraries.length === 1 ? 'plan' : 'plans'}`}
                          </p>
                        )}
                      </div>
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Saved Itineraries Step */}
          {step === 'savedItineraries' && (
            <motion.div 
              key="savedItineraries"
              className="absolute inset-0 overflow-y-auto"
              initial={{ x: getAnimationDirection('savedItineraries') === 'forward' ? '100%' : '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: getAnimationDirection('savedItineraries') === 'forward' ? '-100%' : '100%' }}
              transition={{ type: 'tween', ease: 'easeInOut', duration: 0.3 }}
            >
              <div className="p-3 space-y-4">
                <div className="mt-6 flex justify-end px-1">
                <button
                  onClick={() => setStep('details')}
                  className="px-4 py-2 rounded-full transition-colors flex items-center gap-2"
                  style={{
                    color: 'var(--planner-text-primary)',
                    backgroundColor: '#f3f4f6'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                >
                  <ChevronLeft size={18} />
                  Back
                </button>
              </div>
                <div className="space-y-4">
                  {savedItineraries.map((itinerary) => (
                    <button
                      key={itinerary.id}
                      onClick={() => handleSavedItineraryClick(itinerary)}
                      className="flex flex-col p-4 transition-colors text-left"
                      style={{
                        width: 'var(--planner-button-width)',
                        backgroundColor: 'white',
                        border: '1px solid var(--planner-border-color)',
                        borderRadius: '0.5rem',
                        ...textStyles.base
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--planner-bg-hover)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <h4 style={{...textStyles.medium, ...textStyles.h4}}>
                        {itinerary.tripDetails?.destination}
                      </h4>
                      {/* <div 
                        className="flex items-center gap-2 mt-1"
                        style={{...textStyles.base, ...textStyles.small, ...textStyles.secondary}}>
                        <Calendar style={{ width: 'var(--planner-icon-size-sm)', height: 'var(--planner-icon-size-sm)' }} />
                        <span>
                          {itinerary.tripDetails?.startDate && new Date(itinerary.tripDetails.startDate).toLocaleDateString()} - {' '}
                          {itinerary.tripDetails?.endDate && new Date(itinerary.tripDetails.endDate).toLocaleDateString()}
                        </span>
                      </div> */}
                      <div 
                        className="flex items-center gap-2 mt-1"
                        style={{...textStyles.base, ...textStyles.small, ...textStyles.secondary}}>
                        <Calendar style={{ width: 'var(--planner-icon-size-sm)', height: 'var(--planner-icon-size-sm)' }} />
                        <span>
                          {itinerary.tripDetails?.startDate && itinerary.tripDetails?.endDate && (() => {
                            const start = new Date(itinerary.tripDetails.startDate);
                            const end = new Date(itinerary.tripDetails.endDate);
                            const startMonth = format(start, 'MMMM');
                            const endMonth = format(end, 'MMMM');
                            
                            if (startMonth === endMonth) {
                              return `${format(start, 'd')} - ${format(end, 'd MMMM')}`;
                            } else {
                              return `${format(start, 'd MMMM')} - ${format(end, 'd MMMM')}`;
                            }
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1" style={{...textStyles.small, ...textStyles.secondary}}>
                        <Users style={{ width: 'var(--planner-icon-size-sm)', height: 'var(--planner-icon-size-xs)' }} />
                        <span>{itinerary.tripDetails?.travelGroup || 'Solo traveler'}</span>
                      </div>
                      {itinerary.days && (
                        <div className="mt-2" style={{...textStyles.small, ...textStyles.secondary}}>
                          {itinerary.days.length} {itinerary.days.length === 1 ? 'day' : 'days'}, {
                            itinerary.days.reduce((count, day) => count + (day.activities?.length || 0), 0)
                          } activities
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Details Step */}
          {step === 'details' && (
            <motion.div 
              key="details"
              className="absolute inset-0 overflow-y-auto"
              initial={{ x: getAnimationDirection('details') === 'forward' ? '100%' : '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: getAnimationDirection('details') === 'forward' ? '-100%' : '100%' }}
              transition={{ type: 'tween', ease: 'easeInOut', duration: 0.3 }}
            >
              <div className="p-6 space-y-8">
                {/* Location Search */}
                <div style={{ width: 'var(--planner-input-width-full)' }}>
                  <label 
                    className="block"
                    style={{
                      ...textStyles.base,
                      ...textStyles.medium,
                      fontWeight: 'var(--planner-font-weight-large)',
                      marginBottom: '0.5rem'
                    }}
                  >
                    Where to?
                  </label>
                  <LocationSearch
                    value={formData.destination}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, destination: value }));
                    }}
                  />
                </div>
                {/* Date Range */}
                <DateRangePicker
                  startDate={formData.startDate}
                  endDate={formData.endDate}
                  onChange={(start, end) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      startDate: start ? new Date(start) : null,
                      endDate: end ? new Date(end) : null
                    }));
                  }}
                />
                {/* Interests */}
                <InterestSelector
                  selected={formData.interests}
                  onChange={(types) => {
                    setFormData(prev => ({ ...prev, interests: types }));
                  }}
                />

                {/* Travel Group */}
                <TravelGroupSelector
                  selected={formData.travelGroup}
                  onChange={(group) => {
                    setFormData(prev => ({ ...prev, travelGroup: group }));
                  }}
                />

                {/* Error Display */}
                {error && (
                  <div 
                    className="p-3 bg-red-50 text-red-700 rounded-full"
                    style={{
                      fontSize: 'var(--planner-small-size)',
                      fontFamily: 'var(--chat-font-family)'
                    }}
                  >
                    {error}
                  </div>
                )}

                {/* Submit and Back Buttons */}
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={handleSubmit}
                    disabled={!isValid || isLoading || isGeneratingItinerary}
                    className={`flex-1 px-5 py-3 text-white rounded-full transition-colors whitespace-nowrap ${
                      !isValid || isLoading || isGeneratingItinerary
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                    style={{ 
                      fontSize: 'var(--planner-body-size)',
                      minHeight: '48px' // or whatever height you want
                    }}
                  >
                    {(isLoading || isGeneratingItinerary) ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Creating...</span>
                      </div>
                    ) : (
                      'Create Itinerary'
                    )}
                  </button>
                  
                  {/* UPDATED BACK BUTTON */}
                  <button
                    onClick={() => {
                      console.log('[PlannerWizard] Back button clicked, came from:', cameFromStep);
                      setIsNavigatingBack(true);
                      
                      // Clear the itinerary data to prevent auto-transition
                      if (onItineraryUpdate) {
                        onItineraryUpdate({
                          tripDetails: {
                            destination: '',
                            travelGroup: ''
                          },
                          days: []
                        }, false);
                      }

                      // Clear form data regardless
                      setFormData({
                        destination: '',
                        startDate: null,
                        endDate: null,
                        interests: []
                      });
                      setSavedItineraryId(null);
                      setFormSubmitted(false);
                      
                      // Go back to where we came from (NOT always initial)
                      setStep(cameFromStep);
                      
                      setTimeout(() => {
                        setIsNavigatingBack(false);
                      }, 500);
                    }}
                    className="px-5 py-3 rounded-full transition-colors"
                    style={{
                      width: 'var(--planner-button-width)',
                      color: 'var(--planner-text-primary)',
                      backgroundColor: '#f3f4f6',
                      fontSize: 'var(--planner-body-size)',
                      maxWidth: '40%'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  >
                    Back
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Personalized Planning Step */}
          {step === 'personalized' && (
            <motion.div 
              key="personalized"
              className="absolute inset-0"
              initial={{ x: getAnimationDirection('personalized') === 'forward' ? '100%' : '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: getAnimationDirection('personalized') === 'forward' ? '-100%' : '100%' }}
              transition={{ type: 'tween', ease: 'easeInOut', duration: 0.3 }}
            >
              <PersonalizedPlannerPanel 
                isVisible={true}
                onToggleVisibility={() => {}}
                onLocationSelect={handleLocationSelect}
                selectedLocationId={selectedLocationId}
                tripDetails={itinerary.tripDetails}
                onTripDetailsUpdate={handleTripDetailsUpdate}
                onLocationsUpdate={handleLocationsUpdate}
              />
            </motion.div>
          )}

          {/* Results Step */}
          {step === 'results' && (
            <motion.div 
              key="results"
              className="absolute inset-0"
              initial={{ x: getAnimationDirection('results') === 'forward' ? '100%' : '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: getAnimationDirection('results') === 'forward' ? '-100%' : '100%' }}
              transition={{ type: 'tween', ease: 'easeInOut', duration: 0.3 }}
            >
              <div className="h-full relative">
                {/* Two buttons for clear navigation */}
                {/* Two buttons for clear navigation - stacked and right aligned */}
                <div className="absolute top-3 right-3 z-10 flex flex-col gap-1">
                  <button
                    onClick={() => {
                      setIsNavigatingBack(true);
                      setStep('details');
                      setTimeout(() => {
                        setIsNavigatingBack(false);
                      }, 500);
                    }}
                    className="px-2 py-2 bg-white/80 rounded-full flex items-center gap-1 hover:bg-white shadow-sm text-xs"
                  >
                    <Edit size={14} />
                   
                  </button>
                  
                  <button
                    onClick={() => {
                      setIsNavigatingBack(true);
                      // Clear data when going to saved itineraries
                      if (onItineraryUpdate) {
                        onItineraryUpdate({
                          tripDetails: { destination: '', travelGroup: '' },
                          days: []
                        }, false);
                      }
                      setStep('savedItineraries');
                      setTimeout(() => {
                        setIsNavigatingBack(false);
                      }, 500);
                    }}
                    className="px-2 py-2 bg-white/80 rounded-full flex items-center gap-1 hover:bg-white shadow-sm text-xs"
                  >
                    <BookOpen size={14} />
                    
                  </button>
                </div>
                
                {!showItineraryContent ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center p-6">
                      <div className="w-16 h-16 border-4 border-t-blue-500 border-blue-200 rounded-full animate-spin mx-auto mb-4"></div>
                      <p style={{
                        ...textStyles.base,
                        fontSize: 'var(--planner-subheading-size)',
                        fontWeight: 'var(--planner-font-weight-medium)',
                        marginBottom: '0.5rem'
                      }}>
                        Building your itinerary...
                      </p>
                      <p style={{...textStyles.base, ...textStyles.small, ...textStyles.secondary}}>
                        This may take a few moments
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="h-full" data-testid="itinerary-display">
                    <ItineraryPanel
                      itinerary={itinerary}
                      onLocationSelect={onLocationSelect || (() => {})}
                      selectedLocationId={selectedLocationId}
                      onLocationsUpdate={onLocationsUpdate}
                      streamingActivity={streamingActivity}
                      activeDay={activeDay}
                      isVisible={true}
                      onToggleVisibility={undefined}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
);

PlannerWizard.displayName = 'PlannerWizard';
